import { Button } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import { FEED_BOTTOM_ELEM_ID } from './constants';
import { GetEventLogsQueryGQLType } from '../../../types/dashboard-core-gqlTypes.generated.d';
import * as CreditFacilityEntityConfig from '~/core/routes/credit-facilities/EntityConfig';
import * as DeliverableEntityConfig from '~/core/routes/deliverables/EntityConfig';
import * as FundingRequestEntityConfig from '~/core/routes/funding-requests/EntityConfig';
import * as InvoiceEntityConfig from '~/core/routes/invoices/EntityConfig';
import * as LimitedPartnerEntityConfig from '~/core/routes/limited-partners/EntityConfig';
import * as ValleyBankEntityConfig from '~/core/routes/valley-bank/EntityConfig';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { useScrollToAfterAllLoaded } from '~/packages/common-frontend/hooks/useScrollToAfterAllLoaded';

// Exports
type EventLog = GetEventLogsQueryGQLType['eventLogs']['items'][number];
type EventFeedProps = {
  logs: EventLog[];
  direction?: 'newest-last' | 'newest-first';
  showAllCaughtUp?: boolean;
  hasMore: boolean;
  isFetchingMore?: boolean;
  fetchMore: (cursor: string) => void;
  onThreadAction?: () => void;
};
export const EventFeed = ({
  logs,
  direction = 'newest-last',
  showAllCaughtUp = true,
  hasMore,
  isFetchingMore,
  fetchMore,
  onThreadAction,
}: EventFeedProps) => {
  const { onReady, onMount } = useScrollToAfterAllLoaded({
    behavior: 'instant',
    scrollTimeout: 250,
    scrollToElemSelector: `#${FEED_BOTTOM_ELEM_ID}`,
  });

  const renderedHasMoreButton = (
    <>
      {hasMore && (
        <Button
          key="has-more-button"
          size="sm"
          variant="secondary-muted"
          flexShrink={0}
          width="100%"
          marginTop={direction === 'newest-first' ? '1rem' : '0rem'}
          marginBottom={direction === 'newest-last' ? '1rem' : '0rem'}
          isLoading={isFetchingMore}
          onClick={() => {
            const before = logs.at(-1)?.eventId;

            if (!before) {
              Sentry.captureMessage(
                `Failed to find "before" cursor in EventFeed pagination "fetchMore" callback`,
                Sentry.Severity.Warning,
              );
              return;
            }

            fetchMore(before);
          }}
        >
          Show Older Events
        </Button>
      )}
    </>
  );

  const renderedAllCaughtUp = (
    <>
      {showAllCaughtUp && (
        <Typography
          variant="detail-muted"
          TextProps={{
            id: FEED_BOTTOM_ELEM_ID,
            textAlign: 'center',
            marginBlock: '1.5rem',
          }}
        >
          All caught up! 🎉
        </Typography>
      )}
    </>
  );

  const orderedLogs = direction === 'newest-last' ? [...logs].reverse() : logs;
  return (
    <>
      {direction === 'newest-last'
        ? renderedHasMoreButton
        : renderedAllCaughtUp}

      {orderedLogs.map((log) => (
        <React.Fragment key={log.eventId}>
          <CreditFacilityEntityConfig.EventFeedItem
            log={log}
            onMount={onMount}
            onReady={onReady}
            onThreadAction={onThreadAction}
          />
          <DeliverableEntityConfig.EventFeedItem
            log={log}
            onMount={onMount}
            onReady={onReady}
            onThreadAction={onThreadAction}
          />
          <FundingRequestEntityConfig.EventFeedItem
            log={log}
            onMount={onMount}
            onReady={onReady}
            onThreadAction={onThreadAction}
          />
          <InvoiceEntityConfig.EventFeedItem
            log={log}
            onMount={onMount}
            onReady={onReady}
            onThreadAction={onThreadAction}
          />
          <LimitedPartnerEntityConfig.EventFeedItem
            log={log}
            onMount={onMount}
            onReady={onReady}
            onThreadAction={onThreadAction}
          />
          <ValleyBankEntityConfig.EventFeedItem
            log={log}
            onMount={onMount}
            onReady={onReady}
            onThreadAction={onThreadAction}
          />
        </React.Fragment>
      ))}
      {orderedLogs.length === 0 && !showAllCaughtUp && (
        <Typography
          variant="detail-muted"
          TextProps={{ textAlign: 'center', marginBlock: '1.5rem' }}
        >
          No events found.
        </Typography>
      )}

      {direction === 'newest-first'
        ? renderedHasMoreButton
        : renderedAllCaughtUp}
    </>
  );
};
